import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Breadcrumb from '../../components/common/Breadcrumb';
import { PageLoader } from '../../components/common/Loader';
import {
    IconPlus, IconChevronRight, IconRefresh,
    IconCheck, IconClock, IconLoader, IconClipboardList,
} from '@tabler/icons-react';

import { useMy360Requests, usePending360Nominations } from '../../hooks/usePerformance';

const AVATAR_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#a855f7', '#06b6d4', '#3b82f6', '#f43f5e'];
function avatarColor(str = '') { return AVATAR_COLORS[(str.charCodeAt(0) || 0) % AVATAR_COLORS.length]; }
function initials(name = '') { return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?'; }

const RATER_COLORS = {
    self:       { bg: 'bg-primary/10',  text: 'text-primary',  label: 'Self'          },
    supervisor: { bg: 'bg-info/10',     text: 'text-info',     label: 'Supervisor'    },
    supervisee: { bg: 'bg-success/10',  text: 'text-success',  label: 'Direct Report' },
    colleague:  { bg: 'bg-warning/10',  text: 'text-warning',  label: 'Colleague'     },
    external:   { bg: 'bg-purple/10',   text: 'text-purple',   label: 'External'      },
};

function formatDate(d) {
    if (!d) return '–';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const STATUS_DOT = {
    draft:  '#FC985E',
    active: 'var(--success)',
    closed: 'var(--success)',
};
const STATUS_LABEL = {
    draft:  'Draft',
    active: 'Active',
    closed: 'Closed',
};
const STATUS_CLASS = {
    draft:  'bg-warning/10 text-warning border-warning/20',
    active: 'bg-success/10 text-success border-success/20',
    closed: 'bg-success/10 text-success border-success/20',
};

function StatusPill({ status }) {
    const cls   = STATUS_CLASS[status] || STATUS_CLASS.draft;
    const dot   = STATUS_DOT[status]   || STATUS_DOT.draft;
    const label = STATUS_LABEL[status] || status;
    return (
        <span className={`inline-flex items-center gap-5 px-8 py-3 rounded-full text-[11px] font-semibold border whitespace-nowrap ${cls}`}>
            <span className="w-[6px] h-[6px] rounded-full flex-shrink-0" style={{ backgroundColor: dot }} />
            {label}
        </span>
    );
}

export default function Feedback360() {
    const navigate = useNavigate();
    const location = useLocation();
    const [tab, setTab] = useState('mine');

    useEffect(() => {
        if (location.state?.tab) {
            setTab(location.state.tab);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, []);

    const { data: myRequests = [], isLoading: loadingMine } = useMy360Requests();
    const { data: pending = [], isLoading: loadingPending } = usePending360Nominations();

    if (loadingMine && tab === 'mine') return <PageLoader text="Loading your 360 reviews..." />;

    return (
        <div className="pb-30">
            <div className="mb-20">
                <Breadcrumb title="360-Degree Feedback" path="Performance" />
            </div>

            {/* Tabs */}
            <div className="flex gap-0 border-b border-border mb-20">
                {[
                    { key: 'mine',    label: 'My Reviews' },
                    { key: 'pending', label: 'Pending Feedback', count: pending.length },
                ].map(t => (
                    <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        className={`flex items-center gap-8 px-16 py-10 text-sm font-medium border-b-2 transition-colors -mb-px ${
                            tab === t.key
                                ? 'border-primary text-primary'
                                : 'border-transparent text-text-secondary hover:text-text-primary'
                        }`}
                    >
                        {t.label}
                        {t.count > 0 && (
                            <span className="bg-primary text-white text-[10px] font-bold rounded-full w-18 h-18 flex items-center justify-center leading-none">
                                {t.count}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* My Reviews */}
            {tab === 'mine' && (
                <>
                    {loadingMine ? (
                        <div className="py-40 text-center text-text-secondary">
                            <IconLoader className="animate-spin inline-block mb-8" size={22} />
                            <p className="text-sm">Loading...</p>
                        </div>
                    ) : myRequests.length === 0 ? (
                        <div className="bg-bg-dark rounded-lg border border-border shadow-light py-60 text-center">
                            <div className="w-48 h-48 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-15">
                                <IconClipboardList className="text-primary" size={22} />
                            </div>
                            <p className="text-sm font-semibold text-text-primary mb-5">No 360 reviews yet</p>
                            <p className="text-[11px] text-text-secondary mb-20">
                                Initiate a review cycle and collect feedback from peers, your manager, and direct reports.
                            </p>
                            <button
                                onClick={() => navigate('/performance/360-feedback/new')}
                                className="btn btn-primary"
                            >
                                <IconPlus size={15} /> Start New Review
                            </button>
                        </div>
                    ) : (
                        <div className="bg-bg-dark rounded-lg border border-border shadow-light overflow-hidden">
                            {myRequests.map((req, i) => (
                                <button
                                    key={req.id}
                                    onClick={() => navigate(`/performance/360-feedback/request/${req.id}`)}
                                    className={`w-full flex items-center gap-10 px-15 py-10 text-left hover:bg-bg-light transition-colors group ${i > 0 ? 'border-t border-border' : ''}`}
                                >
                                    <div className="w-30 h-30 rounded-lg bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                                        <IconRefresh size={14} />
                                    </div>
                                    <div className="flex-grow min-w-0">
                                        <p className="text-[13px] font-semibold text-text-primary truncate">{req.cycle_name}</p>
                                        <div className="flex flex-wrap items-center gap-10 mt-2">
                                            <span className="text-[11px] text-text-secondary">
                                                <span className="font-bold">{req.responded_count ?? 0}</span>/{req.nominations_count ?? 0} responded
                                            </span>
                                            {req.deadline && (
                                                <span className="text-[11px] text-text-secondary flex items-center gap-3">
                                                    <IconClock size={10} /> Due {formatDate(req.deadline)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-8 flex-shrink-0">
                                        <StatusPill status={req.status} />
                                        <IconChevronRight size={13} className="text-text-secondary group-hover:text-primary transition-colors" />
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* Pending Feedback */}
            {tab === 'pending' && (
                <>
                    {loadingPending ? (
                        <div className="py-40 text-center text-text-secondary">
                            <IconLoader className="animate-spin inline-block mb-8" size={22} />
                            <p className="text-sm">Loading...</p>
                        </div>
                    ) : pending.length === 0 ? (
                        <div className="bg-bg-dark rounded-lg border border-border shadow-light py-60 text-center">
                            <div className="w-48 h-48 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-15">
                                <IconCheck className="text-success" size={22} />
                            </div>
                            <p className="text-sm font-semibold text-text-primary mb-5">All caught up!</p>
                            <p className="text-[11px] text-text-secondary">No pending feedback requests at the moment.</p>
                        </div>
                    ) : (
                        <div className="bg-bg-dark rounded-lg border border-border shadow-light overflow-hidden">
                            {pending.map((item, i) => {
                                const raterColor = RATER_COLORS[item.rater_type] || RATER_COLORS.colleague;
                                const subjectName = item.subject_name || item.subject || '—';
                                const cycleName   = item.cycle_name || `Requested ${formatDate(item.created_at)}`;
                                return (
                                    <button
                                        key={item.id}
                                        onClick={() => navigate(`/performance/360-feedback/respond/${item.id}`, { state: { nomination: item } })}
                                        className={`w-full flex items-center gap-12 px-15 py-10 text-left hover:bg-bg-light transition-colors group ${i > 0 ? 'border-t border-border' : ''}`}
                                    >
                                        <div
                                            className="w-[30px] h-[30px] rounded-full flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0"
                                            style={{ backgroundColor: avatarColor(subjectName) }}
                                        >
                                            {initials(subjectName)}
                                        </div>

                                        <div className="flex-grow min-w-0">
                                            <p className="text-[12px] font-semibold text-text-primary truncate leading-tight">{subjectName}</p>
                                            <p className="text-[10px] text-text-secondary truncate mt-1">{cycleName}</p>
                                        </div>

                                        <div className="flex items-center gap-8 flex-shrink-0">
                                            <span className={`text-[9px] font-bold uppercase tracking-widest px-6 py-2 rounded-full ${raterColor.bg} ${raterColor.text}`}>
                                                {item.rater_type_display || raterColor.label}
                                            </span>
                                            <IconChevronRight size={13} className="text-text-secondary group-hover:text-primary transition-colors" />
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
